import { baseUrl } from "./env";
import config from "../config";
import { withLang } from "./langParam";

// 🔐 TEAMS APP SECURITY: Token Manager with security-first approach
// Follows Microsoft's security best practices for Teams apps
class TeamsTokenManager {
  constructor() {
    // SECURITY: All tokens stored in memory only (never localStorage/sessionStorage)
    this.credential = null;
    this.pendingTokenRequests = new Map(); // Scope -> Promise
    this.cachedTokens = new Map(); // Scope -> {token, expiry}
    
    // SECURITY: No persistent token storage - let MSAL handle caching securely
    // MSAL internally handles token caching with proper security measures
  }

  setCredential(credential) {
    this.credential = credential;
    // SECURITY: Clear any pending requests when credential changes
    this.pendingTokenRequests.clear();
    this.cachedTokens.clear();
  }

  clearCredential() {
    this.credential = null;
    this.pendingTokenRequests.clear();
    this.cachedTokens.clear();
  }

  // 🕒 AUTHENTICATION READINESS: Wait for Teams credential to be available
  async waitForCredential(maxWaitMs = 10000) {
    if (this.credential) {
      return; // Already available
    }
    
    console.log('⏳ Waiting for Teams credential to be available...');
    const startTime = Date.now();
    
    while (!this.credential && (Date.now() - startTime) < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, 100)); // Check every 100ms
    }
    
    if (!this.credential) {
      throw new Error('❌ Teams credential not available after waiting ' + maxWaitMs + 'ms');
    }
    
    console.log('✅ Teams credential ready, proceeding with authentication');
  }

  async getToken(targetUrl = null) {
    console.log('🔍 DEBUG: globalTeamsCredential:', this.credential ? 'EXISTS' : 'NULL');
    console.log('🔍 DEBUG: targetUrl:', targetUrl);
    
    // 🕒 AUTHENTICATION READINESS: Wait for credential to be available
    try {
      await this.waitForCredential();
    } catch (error) {
      console.error('❌ Authentication readiness timeout:', error.message);
      return null;
    }

    // ---------- 1. Determine scopes based on target URL ----------
    let scopes;
    if (targetUrl && (targetUrl.startsWith(baseUrl) || targetUrl.includes('izzygoprod-backend.azurewebsites.net'))) {
      // ✅ .NET Backend needs API tokens - using delegated scope
      scopes = [config.apiScope];
      console.log('🔵 Requesting API token for .NET Backend:', scopes[0]);
      console.log('🔴 Target URL:', targetUrl);
      console.log('🔄 baseUrl:', baseUrl);
    } else {
      // ✅ Azure Functions need Graph tokens
      scopes = ['https://graph.microsoft.com/.default'];
      console.log('🟢 Requesting Graph token for Azure Functions');
    }

    const scopeKey = scopes.join(',');

    // SECURITY & PERFORMANCE: Check cached token first
    const cachedToken = this.cachedTokens.get(scopeKey);
    if (cachedToken && cachedToken.expiry > Date.now()) {
      console.log('⚡ Using cached token for scope:', scopeKey);
      return cachedToken.token;
    }

    // SECURITY & PERFORMANCE: Prevent duplicate token requests for same scope
    if (this.pendingTokenRequests.has(scopeKey)) {
      console.log('🔄 Reusing existing token request for scope:', scopeKey);
      return await this.pendingTokenRequests.get(scopeKey);
    }

    // Create new token request
    console.log('🆕 Creating new token request for scopes:', scopes[0]);
    const tokenPromise = this._requestToken(scopes, scopeKey);
    
    // SECURITY: Store promise temporarily to prevent duplicates
    this.pendingTokenRequests.set(scopeKey, tokenPromise);
    
    try {
      const token = await tokenPromise;
      console.log(`✅ Token acquired for scopes: ${scopes.join(', ')}`);
      return token;
    } catch (error) {
      // Clean up failed request
      this.pendingTokenRequests.delete(scopeKey);
      throw error;
    }
  }

  async _requestToken(scopes, scopeKey) {
    try {
      // SECURITY: Let MSAL handle token caching and refresh internally
      // This leverages MSAL's secure token management
      const tokenObj = await this.credential.getToken(scopes);
      const token = tokenObj?.token || tokenObj;
      
      // Cache token for 50 minutes (tokens expire after 60 minutes)
      const expiry = Date.now() + (50 * 60 * 1000);
      this.cachedTokens.set(scopeKey, { token, expiry });
      
      // Clean up pending request
      this.pendingTokenRequests.delete(scopeKey);
      
      return token;
    } catch (error) {
      console.error('Failed to get Teams token:', error);
      
      // Clean up pending request on error
      this.pendingTokenRequests.delete(scopeKey);
      
      // Handle consent requirements
      if (error.errorCode === 'UiRequiredError' || error.message?.includes('need login first')) {
        console.log('🔵 Interactive consent still required - signaling to UI');
        
        const uiErr = new Error('CONSENT_NEEDED');
        uiErr.needConsent = true;
        uiErr.scopes = scopes;
        throw uiErr;
      }
      
      throw error;
    }
  }
}

// SECURITY: Lazy initialization to prevent module load timing issues
let tokenManager = null;

function getTokenManager() {
  if (!tokenManager) {
    tokenManager = new TeamsTokenManager();
  }
  return tokenManager;
}

// Export functions for backward compatibility
export function setTeamsCredential(credential) {
  getTokenManager().setCredential(credential);
}

export function clearTeamsCredential() {
  getTokenManager().clearCredential();
}

async function getAuthToken(targetUrl = null) {
  return await getTokenManager().getToken(targetUrl);
}

const http = {
  post: (path, body) => {
    return new Promise(async (resolve, reject) => {
      try {
        const fullUrl = `${baseUrl}${path}`;
        const token = await getAuthToken(fullUrl);
        
        if (!token) {
          console.error('❌ POST request cancelled - no authentication token:', fullUrl);
          return reject({ message: 'Authentication required', url: fullUrl });
        }
        
        const headers = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        };
        
        const response = await fetch(fullUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
        
        // Handle 401 - signal need for consent
        if (response.status === 401) {
          const uiErr = new Error('CONSENT_NEEDED');
          uiErr.needConsent = true;
          uiErr.scopes = [config.apiScope]; // Use delegated scope
          throw uiErr;
        }
        
        const data = await response.json();
        if (response.ok) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (error) {
        // Propagate consent errors
        if (error.needConsent) {
          reject(error);
        } else {
          reject(error);
        }
      }
    });
  },

  // Fire-and-forget POST that survives tab/modal close via keepalive, WITH the Bearer
  // header (sendBeacon can't set auth). Used for telemetry flushes (spec §4). Never throws.
  postKeepalive: async (path, body) => {
    try {
      const fullUrl = `${baseUrl}${path}`;
      const token = await getAuthToken(fullUrl);
      if (!token) return; // no token → silently drop, never block the UI
      await fetch(fullUrl, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    } catch (_) { /* telemetry is best-effort */ }
  },

  get: (path) => {
    return new Promise(async (resolve, reject) => {
      // Central content-language wiring: every GET carries ?lang (non-EN) so translated
      // content (ContentTranslations) is served. Endpoints without lang support ignore it.
      const fullUrl = `${baseUrl}${withLang(path)}`;
      const token = await getAuthToken(fullUrl);
      
      if (!token) {
        console.error('❌ GET request cancelled - no authentication token:', fullUrl);
        return reject({ message: 'Authentication required', url: fullUrl });
      }
      
      const headers = { "Authorization": `Bearer ${token}` };
      
      // Determine scopes for potential 401 retry
      let retryScopes;
      if (fullUrl.includes(baseUrl) || fullUrl.includes('izzygoprod-backend.azurewebsites.net')) {
        retryScopes = [config.apiScope]; // Use delegated scope
      } else {
        retryScopes = ['https://graph.microsoft.com/.default'];
      }
      
      try {
        let response = await fetch(fullUrl, {
          method: "GET",
          headers,
        });
        
        // 🔄 Handle 401 - signal need for consent
        if (response.status === 401) {
          const uiErr = new Error('CONSENT_NEEDED');
          uiErr.needConsent = true;
          uiErr.scopes = retryScopes; // Use the scopes we determined earlier
          throw uiErr;
        }
        
        const data = await response.json();
        if (response.ok) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  postFormData: (path, body) => {
    return new Promise(async (resolve, reject) => {
      const fullUrl = `${baseUrl}${path}`;
      const token = await getAuthToken(fullUrl);
      try {
        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, 
          },
          body, 
        });

        const data = await response.json();

        if (response.ok) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  patch: (path, body) => {
    return new Promise(async (resolve, reject) => {
      const fullUrl = `${baseUrl}${path}`;
      const token = await getAuthToken(fullUrl);
      const headers = {
        "Authorization": `Bearer ${token}`,
      };
      try {
        const response = await fetch(fullUrl, {
          method: "PATCH",
          headers,
          body, 
        });
        const data = await response.json();
        if (response.ok) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  delete: (path, body) => {
    return new Promise(async (resolve, reject) => {
      const fullUrl = `${baseUrl}${path}`;
      const token = await getAuthToken(fullUrl);
      const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
      try {
        const response = await fetch(fullUrl, {
          method: "DELETE",
          headers,
          body: JSON.stringify(body), 
        });
        const data = await response.json();
        if (response.ok) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (error) {
        reject(error);
      }
    });
  }
}
export default http;
